import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ListFetchError from "../components/ListFetchError";
import { apiRequest, API_BASE_URL } from "../api/client";

function About({ onOpenAuth, onOpenMenu }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [about, setAbout] = useState(null);

  const fetchAbout = async () => {
    setLoading(true);
    setError("");
    const res = await apiRequest("api/page_sections?page_module=about_us", { method: "GET" });
    if (!res.ok) {
      setError(res.data?.message || "Failed to load About Us content.");
      setAbout(null);
    } else {
      setAbout(res.data || null);
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await apiRequest("api/page_sections?page_module=about_us", { method: "GET" });
      if (!alive) return;
      if (!res.ok) {
        setError(res.data?.message || "Failed to load About Us content.");
        setAbout(null);
      } else {
        setAbout(res.data || null);
      }
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setError("Failed to load About Us content.");
      setAbout(null);
      setLoading(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  const heroSection = useMemo(() => {
    const first = Array.isArray(about?.page_sections) ? about.page_sections[0] : null;
    return first && typeof first === "object" ? first : null;
  }, [about?.page_sections]);

  const grouped = useMemo(() => {
    return Array.isArray(about?.grouped_sections) ? about.grouped_sections : [];
  }, [about?.grouped_sections]);

  const itemsByType = useMemo(() => {
    const map = new Map();
    grouped.forEach((g) => {
      const key = String(g?.section_type || "").trim();
      const items = Array.isArray(g?.items) ? g.items : [];
      if (key) map.set(key, items);
    });
    return map;
  }, [grouped]);

  const heroImageUrl = useMemo(() => {
    const raw = heroSection?.image_url;
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${API_BASE_URL.replace(/\/$/, "")}${raw.startsWith("/") ? "" : "/"}${raw}`;
  }, [heroSection?.image_url]);

  const resolveImageUrl = (raw) => {
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return `${API_BASE_URL.replace(/\/$/, "")}${value.startsWith("/") ? "" : "/"}${value}`;
  };

  const title = heroSection?.title?.trim?.() ? heroSection.title : "About Us";
  const subTitle = heroSection?.sub_title?.trim?.() ? heroSection.sub_title : "";
  const heroDescription = heroSection?.description?.trim?.() ? heroSection.description : "";

  const featuresData = itemsByType.get("features_data") || [];
  const whatWeDoData = itemsByType.get("what_we_do_data") || [];
  const stepsData = itemsByType.get("steps_data") || [];
  const privacyPolicyData = itemsByType.get("privacy_policy_data") || [];

  const featureByTitle = useMemo(() => {
    const m = new Map();
    featuresData.forEach((item) => {
      const key = String(item?.title || "").trim().toLowerCase();
      if (key) m.set(key, item);
    });
    return m;
  }, [featuresData]);

  const whoWeAre = featureByTitle.get("who we are") || null;
  const ourMission = featureByTitle.get("our mission") || null;
  const builtOnTrust = featureByTitle.get("built on trust") || null;
  const ourPromise = featureByTitle.get("our promise") || null;

  const privacyPromise = useMemo(() => {
    const first = Array.isArray(privacyPolicyData) ? privacyPolicyData[0] : null;
    return first && typeof first === "object" ? first : null;
  }, [privacyPolicyData]);

  const renderText = (text) => {
    const value = typeof text === "string" ? text : "";
    return value.replace(/\r\n/g, "\n").trim();
  };

  const whatWeDoIcon = (key) => {
    const k = String(key || "").trim().toLowerCase();
    const common = "h-8 w-8";
    if (k.includes("compare")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 7h10M7 12h6M7 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    if (k.includes("save")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2l2.1 6.4H21l-5.6 4.1L17.5 19 12 14.9 6.5 19l2.1-6.5L3 8.4h6.9L12 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (k.includes("simplify")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 7h10M7 12h10M7 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 4h16v16H4V4Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z" stroke="currentColor" strokeWidth="2" />
        <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const stepIcon = (titleText) => {
    const k = String(titleText || "").toLowerCase();
    const common = "h-8 w-8";
    if (k.includes("privacy")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z" stroke="currentColor" strokeWidth="2" />
          <path d="M9.5 11.5a2.5 2.5 0 0 1 5 0V14H9.5v-2.5Z" stroke="currentColor" strokeWidth="2" />
          <path d="M9 14h6v4H9v-4Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  };

  const featureIcon = (titleText) => {
    const k = String(titleText || "").trim().toLowerCase();
    const common = "h-10 w-10";
    if (k.includes("mission")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="m9 12 2 2 4-5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (k.includes("trust")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (k.includes("promise")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12.1 20.3 4.6 13a5 5 0 0 1 0-7.1 5 5 0 0 1 7.1 0l.3.3.3-.3a5 5 0 0 1 7.1 0 5 5 0 0 1 0 7.1l-7.5 7.3a1 1 0 0 1-1.4 0Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M4 20a8 8 0 0 1 16 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8">
        {loading ? (
          <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
            <p className="text-[16px] text-[#6E7B86]">Loading...</p>
          </section>
        ) : error ? (
          <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
            <ListFetchError message={error} onRetry={fetchAbout} isLoading={loading} />
          </section>
        ) : (
          <div className="space-y-6">
            {/* Hero */}
            <section className="overflow-hidden rounded-2xl border border-[#E6EAF0] bg-white shadow-sm md:rounded-3xl">
              <div className="grid gap-5 p-4 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8 lg:p-10">
                <div className="flex flex-col justify-center">
                  <h1 className="text-[24px] font-semibold leading-tight tracking-tight text-[#24303A] sm:text-[26px] md:text-[28px]">
                    {title}
                  </h1>
                  {subTitle ? (
                    <p className="mt-2 text-[16px] leading-relaxed text-[#6E7B86] sm:text-[15px] md:mt-3 md:text-[16px]">
                      {renderText(subTitle)}
                    </p>
                  ) : null}
                  {heroDescription ? (
                    <p className="mt-2 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86] sm:text-[16px] md:mt-3 md:text-[16px]">
                      {renderText(heroDescription)}
                    </p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap gap-2 md:mt-5 md:gap-3">
                    <a
                      href="/how-it-works"
                      className="inline-flex items-center justify-center rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-semibold text-white sm:px-5  md:text-[16px]"
                    >
                      How it works
                    </a>
                  </div>
                </div>
                <div className="relative">
                  {heroImageUrl ? (
                    <img
                      src={heroImageUrl}
                      alt={title}
                      className="h-[200px] w-full rounded-xl object-cover sm:h-[240px] md:h-full md:min-h-[320px] md:rounded-2xl"
                    />
                  ) : (
                    <div className="h-[200px] w-full rounded-xl border border-[#E6EAF0] bg-gradient-to-br from-[#F6F3FF] via-[#F5F7FB] to-[#FFF4EA] sm:h-[240px] md:h-full md:min-h-[320px] md:rounded-2xl" />
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="border-t border-[#E6EAF0] bg-[#FBFCFF] p-4 sm:p-6 md:p-8">
                <div className="grid gap-4 md:grid-cols-1">
                  {[...(featuresData || [])]
                    .sort((a, b) => (Number(a?.position ?? 0) - Number(b?.position ?? 0)) || String(a?.title || "").localeCompare(String(b?.title || "")))
                    .map((item, idx) => (
                      <div key={item?.id ?? idx} className="rounded-xl border border-[#E6EAF0] bg-white p-4 sm:p-5 md:rounded-2xl">
                        <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4 md:gap-6">
                            {resolveImageUrl(item?.image_url) ? (
                              <img
                                src={resolveImageUrl(item?.image_url)}
                                alt={item?.title || "Feature icon"}
                                className="h-12 w-12 object-contain sm:h-14 sm:w-14"
                                loading="lazy"
                              />
                            ) : (
                              featureIcon(item?.title)
                            )}
                          
                          <div>
                            <h2 className="text-[17px] font-semibold text-[#24303A] sm:text-[18px]">{item?.title || ""}</h2>
                            <p className="mt-1 w-full whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86] sm:text-[15px] md:text-[16px]">
                              {renderText(item?.description)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>

            {/* What we do */}
            <section className="rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-6 md:rounded-3xl md:p-10">
              <h2 className="text-[18px] font-semibold text-[#24303A] sm:text-[20px]">What we do</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4 lg:gap-6">
                {(whatWeDoData.length ? whatWeDoData : []).map((item, idx) => (
                  <div
                    key={item?.id ?? idx}
                    className="rounded-xl border border-[#E6EAF0] bg-white p-4 text-center shadow-sm sm:p-5 md:rounded-2xl md:shadow-md"
                  >
                    <div className="mx-auto inline-flex items-center justify-center text-[#7352C7]">
                      {resolveImageUrl(item?.image_url) ? (
                        <img
                          src={resolveImageUrl(item?.image_url)}
                          alt={item?.title || "What we do icon"}
                          className="h-12 w-12 object-contain sm:h-14 sm:w-14"
                          loading="lazy"
                        />
                      ) : (
                        whatWeDoIcon(item?.title)
                      )}
                    </div>
                    <h3 className="mt-2 text-[17px] font-semibold text-[#24303A] sm:mt-3 sm:text-[18px]">
                      {item?.title || ""}
                    </h3>
                    <p className="mt-1 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86] sm:text-[15px] md:text-[16px]">
                      {renderText(item?.description)}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Why trust FEMTYZ */}
            <section className="rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-6 md:rounded-3xl md:p-10">
              <h2 className="text-[18px] font-semibold text-[#24303A] sm:text-[20px]">Why trust FEMTYZ?</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4 lg:gap-8">
                {(stepsData.length ? stepsData : []).map((item, idx) => (
                  <div
                    key={item?.id ?? idx}
                    className="flex flex-col items-center gap-3 rounded-xl border border-[#E6EAF0] bg-white p-4 sm:gap-4 md:rounded-2xl md:gap-5"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center text-[#7352C7] sm:h-14 sm:w-14">
                      {resolveImageUrl(item?.image_url) ? (
                        <img
                          src={resolveImageUrl(item?.image_url)}
                          alt={item?.title || "Trust icon"}
                          className="h-8 w-8 object-contain sm:h-10 sm:w-10"
                          loading="lazy"
                        />
                      ) : (
                        stepIcon(item?.title)
                      )}
                    </div>
                    <div>
                      <p className="text-center text-[16px] font-medium text-[#24303A] sm:text-[16px]">{item?.title || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {privacyPromise?.title || privacyPromise?.description ? (
              <section className="relative overflow-hidden rounded-2xl border border-[#E6EAF0] bg-[#F6F3FF] p-4 pb-8 shadow-sm sm:p-6 sm:pb-10 md:rounded-3xl md:p-10 md:pb-12">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:gap-4">
                  <div className="inline-flex h-10 w-10 items-center justify-center text-[#7352C7] sm:h-12 sm:w-12">
                    {resolveImageUrl(privacyPromise?.image_url) ? (
                      <img
                        src={resolveImageUrl(privacyPromise?.image_url)}
                        alt={privacyPromise?.title || "Our Promise"}
                        className="h-8 w-8 object-contain sm:h-10 sm:w-10"
                        loading="lazy"
                      />
                    ) : (
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M12.1 20.3 4.6 13a5 5 0 0 1 0-7.1 5 5 0 0 1 7.1 0l.3.3.3-.3a5 5 0 0 1 7.1 0 5 5 0 0 1 0 7.1l-7.5 7.3a1 1 0 0 1-1.4 0Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-[18px] font-semibold text-[#7352C7] sm:text-[20px]">{privacyPromise?.title || ""}</h2>
                    <p className="mt-1 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86] sm:mt-2 sm:text-[15px] md:text-[16px]">
                      {renderText(privacyPromise?.description)}
                    </p>
                  </div>
                </div>

                <div className="pointer-events-none absolute bottom-2 left-1/2 hidden -translate-x-1/2 text-[#7352C7] opacity-90 sm:block sm:bottom-3">
                  <svg className="h-7 w-14" viewBox="0 0 64 24" fill="none" aria-hidden="true">
                    <path
                      d="M18 6c-6 0-10 4-10 6s4 6 10 6c10 0 18-12 28-12 6 0 10 4 10 6s-4 6-10 6c-10 0-18-12-28-12Z"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </section>
            ) : null}

          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default About;
